/**
 * Session state.
 *
 * The API holds the session in an HttpOnly cookie, so the SPA cannot inspect
 * it — `auth.me()` on boot is the only way to know whether we are signed in,
 * and a 401 from it is the normal signed-out answer rather than an error.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiRequestError, auth, type SessionUser } from './api';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  /** Re-reads /auth/me; used after a link or code verification. */
  reload: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  const reload = useCallback(async () => {
    try {
      const result = await auth.me();
      setUser(result.user);
      setStatus('authenticated');
    } catch (error) {
      // Anything other than "no session" is still a signed-out UI: there is no
      // useful screen to show a logged-out user besides sign-in.
      if (!(error instanceof ApiRequestError)) console.error('session check failed', error);
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<SessionValue>(() => ({ status, user, reload, signOut }), [status, user, reload, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}
